import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useConfig, useIncident, useBoard } from "@/hooks/useData";

export function FinishCard({ incidentId, close, onFinished }) {
  const qc = useQueryClient();
  const incQ = useIncident(incidentId);
  const dispQ = useConfig("dispositions");
  const boardQ = useBoard();
  const [dispositionId, setDispositionId] = useState("");
  const [unitStatuses, setUnitStatuses] = useState({});
  const [busy, setBusy] = useState(false);
  const inc = incQ.data;
  const statuses = boardQ.data?.statuses || [];
  const units = boardQ.data?.units || [];

  const dispositions = useMemo(() => {
    const all = (dispQ.data || []).filter((d) => d.active !== false);
    const own = all.filter((d) => d.discipline === inc?.discipline);
    return own.length ? own : all;
  }, [dispQ.data, inc?.discipline]);

  if (!inc) return <div className="text-xs text-muted-foreground">Loading…</div>;

  const finish = async () => {
    if (!dispositionId) return;
    setBusy(true);
    try {
      await api.post(`/incidents/${inc.id}/finish`, { disposition_id: dispositionId, unit_statuses: unitStatuses });
      toast.success(`${inc.incident_number} cleared with ${dispositions.find((d) => d.id === dispositionId)?.label}`);
      qc.invalidateQueries({ queryKey: ["board"] });
      qc.invalidateQueries({ queryKey: ["incident", inc.id] });
      onFinished?.();
      close();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="finish-card">
      <div>
        <div className="overline-label">Finish / Clear incident</div>
        <div className="display text-lg font-semibold mono">{inc.incident_number}</div>
        <div className="text-xs text-muted-foreground">{inc.call_type_name} · {inc.location_raw || "Location pending"}</div>
      </div>
      <label className="block">
        <span className="overline-label">Disposition <span className="text-red-500">* required</span></span>
        <select data-testid="finish-disposition-select" value={dispositionId} onChange={(e) => setDispositionId(e.target.value)} className="field-input mt-1">
          <option value="">Select a disposition…</option>
          {dispositions.map((d) => <option key={d.id} value={d.id}>{d.label} ({d.discipline})</option>)}
        </select>
        {!dispositionId && <div className="text-[11px] text-amber-600 dark:text-amber-300 mt-1" data-testid="finish-disposition-required">A disposition is required to clear this call.</div>}
      </label>
      {inc.assigned_units.length > 0 && (
        <div>
          <div className="overline-label mb-1">Resulting unit statuses</div>
          <div className="space-y-1">
            {inc.assigned_units.map((u) => {
              const full = units.find((x) => x.id === u.unit_id);
              const opts = statuses.filter((s) => s.discipline === full?.discipline && s.category !== "committed");
              const def = opts.find((s) => s.system_key === "available") || opts[0];
              return (
                <div key={u.unit_id} className="flex items-center gap-2 text-xs">
                  <span className="mono font-semibold w-12">{u.call_sign}</span>
                  <span className="text-muted-foreground">{u.status_label} →</span>
                  <select data-testid={`finish-unit-status-${u.call_sign}`} value={unitStatuses[u.unit_id] || def?.id || ""} onChange={(e) => setUnitStatuses((s) => ({ ...s, [u.unit_id]: e.target.value }))} className="field-input !h-7 flex-1">
                    {opts.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button data-testid="finish-cancel-button" onClick={close} className="h-8 px-3 rounded-md border text-xs hover:bg-accent">Cancel</button>
        <button data-testid="finish-confirm-button" disabled={!dispositionId || busy} onClick={finish} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50">Finish incident</button>
      </div>
    </div>
  );
}
